// client/src/voice/intentRouter.js

/**
 * routeIntent (Client)
 * Safety-tuned:
 * - NAVIGATE only when it sounds like navigation:
 *    A) user used a nav prefix ("go to", "open", etc.), OR
 *    B) utterance is VERY short (<=2 words) AND matches a known route phrase exactly
 * - Avoids accidental nav on normal sentences (e.g., "why is CTR down on X")
 * - Only navigates to routes that exist in client/src/App.js
 *
 * @param {string} text
 * @param {object} context - { currentPath, advertiserName, role }
 * @returns {object} intent - { type, targetPath?, payload? }
 */
export function routeIntent(text, context = {}) {
  const raw = String(text || "");
  const normalized = raw.toLowerCase().trim();
  if (!normalized) return { type: "IGNORE" };

  // -----------------------
  // Helpers
  // -----------------------
  const startsWithAny = (phrases) => phrases.some((p) => normalized.startsWith(p));

  const NAV_PREFIXES = [
    "go to ",
    "open ",
    "show me ",
    "take me to ",
    "navigate to ",
    "bring me to ",
    "take me ",
    "go ",
  ];

  const BACK_PHRASES = [
    "back",
    "go back",
    "previous page",
    "previous",
    "return",
    "take me back",
  ];

  const DICTATION_ON = [
    "dictation mode",
    "start dictation",
    "enter dictation",
    "begin dictation",
  ];

  const DICTATION_OFF = [
    "stop dictation",
    "exit dictation",
    "end dictation",
    "leave dictation",
  ];

  const tokens = normalized.split(/\s+/).filter(Boolean);
  const wordCount = tokens.length;

  const isNavPrefixCommand = () => startsWithAny(NAV_PREFIXES);

  const stripNavPrefix = () => {
    let out = normalized;
    for (const p of NAV_PREFIXES) {
      if (out.startsWith(p)) {
        out = out.slice(p.length);
        break;
      }
    }
    return out.trim();
  };

  // -----------------------
  // BACK / DICTATION
  // -----------------------
  if (BACK_PHRASES.some((p) => normalized === p || normalized.startsWith(p + " "))) {
    return { type: "NAVIGATE_BACK" };
  }

  if (DICTATION_ON.some((p) => normalized.includes(p))) {
    return { type: "ENTER_DICTATION" };
  }

  if (DICTATION_OFF.some((p) => normalized.includes(p))) {
    return { type: "EXIT_DICTATION" };
  }

  // -----------------------
  // ROUTES (must exist in client App.js)
  // NOTE: Removed overly-broad phrase "ai" to prevent accidental navigation
  // -----------------------
  const ROUTES = [
    // Core
    { phrases: ["dashboard", "home"], path: "/dashboard" },
    { phrases: ["profile hub", "profile", "account"], path: "/profile-hub" },

    // Analytics hubs
    { phrases: ["social intelligence", "social analytics", "social"], path: "/programmatic-analytics" },
    { phrases: ["programmatic analytics", "programmatic", "omni", "omni analytics", "omni intelligence"], path: "/programmatic-analytics" },
    { phrases: ["cinema analytics", "cinema"], path: "/cinema-analytics" },

    // Social channels
    { phrases: ["facebook analytics", "meta analytics", "facebook"], path: "/facebook-analytics" },
    { phrases: ["instagram analytics", "ig analytics", "instagram"], path: "/instagram-analytics" },
    { phrases: ["tiktok analytics", "tiktok"], path: "/tiktok-analytics" },
    { phrases: ["youtube analytics", "youtube"], path: "/youtube-analytics" },
    { phrases: ["x analytics", "twitter analytics", "x", "twitter"], path: "/x-analytics" },
    { phrases: ["google analytics", "google"], path: "/google-analytics" },
    { phrases: ["truth social analytics", "truthsocial analytics", "truth social", "truthsocial"], path: "/truthsocial-analytics" },
    { phrases: ["rumble analytics", "rumble"], path: "/rumble-analytics" },

    // Programmatic channels
    { phrases: ["audio analytics", "audio"], path: "/audio-analytics" },
    { phrases: ["in game analytics", "ingame analytics", "in-game analytics", "ingame", "in game"], path: "/ingame-analytics" },
    { phrases: ["ctv analytics", "ctv"], path: "/ctv-analytics" },
    { phrases: ["video analytics", "video"], path: "/video-analytics" },
    { phrases: ["mobile analytics", "mobile"], path: "/mobile-analytics" },
    { phrases: ["display analytics", "display"], path: "/display-analytics" },
    { phrases: ["dooh analytics", "dooh"], path: "/dooh-analytics" },
    { phrases: ["email analytics", "email"], path: "/email-analytics" },

    // Sherloc/Eden surfaces (client routes)
    { phrases: ["sherloc", "sherlock", "sherloc ai", "eden"], path: "/sherloc-ai" },
    { phrases: ["sherloc reports", "reports"], path: "/sherloc-reports" },
    { phrases: ["ai optimizations", "optimizations"], path: "/ai-optimizations" },

    // Planner / campaigns
    { phrases: ["media planner", "planner"], path: "/sherloc-media-planner" },
    {
      phrases: [
        "upcoming campaigns",
        "campaigns",
        "media campaigns",
        "planned campaigns",
        "campaigns list",
        "sherloc campaigns",
      ],
      path: "/sherloc-media-campaigns",
    },

    // Ops
    { phrases: ["campaign management", "campaigns manager"], path: "/campaign-management" },
    { phrases: ["optimus"], path: "/optimus" },

    // Platform
    { phrases: ["integrations", "integration"], path: "/integrations" },
    // "upload creatives" folds into Creative Studio, which is where a Cortex
    // client uploads creative. /creative-trafficking is a protected Beta
    // surface, so no phrase resolves to it — voice is navigation, and gets the
    // same authorization as a click.
    {
      phrases: ["creative studio", "studio", "creative", "upload creatives"],
      path: "/creative-studio",
    },
    { phrases: ["pantheon", "pantheon network", "intelligence network"], path: "/pantheon" },

    // Billing + account
    { phrases: ["billing", "invoices", "payments", "zoho"], path: "/billing" },
    { phrases: ["notifications"], path: "/notifications" },
    { phrases: ["settings"], path: "/settings" },
    { phrases: ["install app", "install"], path: "/install-app" },

    // Hubs (client has them)
    { phrases: ["genesis", "genesis hub"], path: "/genesis-hub" },
    { phrases: ["nexus", "nexus hub"], path: "/nexus-hub" },
  ];

  const ALLOWED = new Set(ROUTES.map((r) => r.path));

  const PHRASE_TO_PATH = (() => {
    const map = new Map();
    for (const r of ROUTES) {
      for (const p of r.phrases) {
        map.set(String(p).toLowerCase(), r.path);
      }
    }
    return map;
  })();

  const resolveFromPrefix = () => {
    const phrase = stripNavPrefix();

    let bestMatch = null;
    for (const r of ROUTES) {
      for (const p of r.phrases) {
        if (
          (phrase === p || phrase.startsWith(p + " ")) &&
          (!bestMatch || p.length > bestMatch.phrase.length)
        ) {
          bestMatch = { path: r.path, phrase: p };
        }
      }
    }
    if (bestMatch) return bestMatch.path;

    for (const r of ROUTES) {
      for (const p of r.phrases) {
        if (
          normalized.includes(p) &&
          (!bestMatch || p.length > bestMatch.phrase.length)
        ) {
          bestMatch = { path: r.path, phrase: p };
        }
      }
    }

    return bestMatch?.path || null;
  };

  const resolveFromShortExact = () => {
    if (wordCount > 2) return null;
    return PHRASE_TO_PATH.get(normalized) || null;
  };

  // -----------------------
  // NAVIGATION DECISION
  // -----------------------
  let targetPath = null;

  if (isNavPrefixCommand()) {
    targetPath = resolveFromPrefix();
  } else {
    targetPath = resolveFromShortExact();
  }

  if (targetPath && ALLOWED.has(targetPath)) {
    return { type: "NAVIGATE", targetPath };
  }

  // -----------------------
  // DEFAULT: Eden chat
  // -----------------------
  return {
    type: "SHERLOC_CHAT",
    payload: { text: raw, context },
  };
}
